import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Receipt } from "lucide-react";
import {
  Button,
  DetailSection,
  EmptyState,
  ListCard,
  SkeletonList,
  SkeletonText,
  StatusBadge,
  Textarea,
  Timestamp,
  useSubmittingMutation,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { PortalApiError } from "../api/client";
import {
  tpsBillsApi,
  type TpsBill,
  type TpsBillComment,
  type TpsBillStatus,
} from "../api/tpsBills";
import {
  BatchConfirmDialog,
  BatchResultBanner,
  BatchRowCheckbox,
  BatchRowError,
  BatchSelectionHeader,
  useBatchApprove,
} from "../components/BatchApprove";

// TPS Servicing Portal PR-E — the firm's "Bills" review-and-approve queue. Each
// bill is a monthly statement Phoenix prepared on the firm's behalf; the firm
// reviews the rendered PDF and either Approves it (→ posted to the buyer) or
// Returns it with a note (→ Phoenix correction queue). A bill is invisible to
// the buyer until approved — this is the first hard gate. No React Query in the
// portal; load is plain useState and approve/return re-run the list loader.

const STATUS_TONE: Record<TpsBillStatus, StatusTone> = {
  Draft: "neutral",
  TpsApprovalPending: "warn",
  TpsApproved: "progress",
  Posted: "ok",
  Returned: "danger",
};

const STATUS_LABEL: Record<TpsBillStatus, string> = {
  Draft: "Draft",
  TpsApprovalPending: "Awaiting your approval",
  TpsApproved: "Approved",
  Posted: "Posted",
  Returned: "Returned",
};

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

/** "2026-06-30" → "June 2026" (the billing month). */
function periodLabel(periodEnd: string): string {
  const d = new Date(`${periodEnd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return periodEnd;
  return d.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

/** PR-F — the borrower's payment state, surfaced read-only on the bill (the
 *  firm reviews/approves but never acts on payment). Distribution Gate PR-3
 *  refines a Cleared payment into its payout state: once funds clear, the money
 *  sits in the clearing account "awaiting payout" until the TPS approves the
 *  distribution — or shows "returned for correction" if a payout was sent back.
 *  Renders nothing until there's something to show. */
function BillPaymentIndicator({ bill }: { bill: TpsBill }) {
  // A payout the TPS returned for correction (loan-scoped) takes precedence.
  if (bill.payoutState === "returned") {
    return (
      <span className="text-status-warning" data-testid={`tps-bill-payout-returned-${bill.id}`}>
        Payout returned for correction
      </span>
    );
  }
  // Cleared but not yet paid out — the common post-clearing state.
  if (bill.payoutState === "awaiting_payout") {
    return (
      <span className="text-status-success" data-testid={`tps-bill-awaiting-payout-${bill.id}`}>
        Paid — awaiting payout
      </span>
    );
  }
  // `status` is the approval ladder (TpsBillStatus); a future "Paid" value
  // would sit outside that union, so widen the compare to avoid a no-overlap
  // type error while honouring it if the backend ever sends it.
  const isPaid =
    bill.payoutState === "paid_out" ||
    (bill.status as string) === "Paid" ||
    bill.paymentStatus === "Cleared";
  if (isPaid) {
    return (
      <span className="text-status-success" data-testid={`tps-bill-paid-${bill.id}`}>
        Paid{bill.paidAt && <> <Timestamp value={bill.paidAt} /></>}
      </span>
    );
  }
  if (bill.paymentStatus === "Processing") {
    return (
      <span className="text-gray-mid" data-testid={`tps-bill-processing-${bill.id}`}>
        Payment processing
      </span>
    );
  }
  return null;
}

export function TpsBillsTab() {
  const toast = useToast();
  const [rows, setRows] = useState<TpsBill[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const openBill = params.get("bill");

  const loadList = useCallback(async () => {
    const data = await tpsBillsApi.list();
    setRows(data);
    return data;
  }, []);

  // SP-2b — checkbox batch approve over the awaiting-approval bills.
  const batch = useBatchApprove({
    approve: (ids) => tpsBillsApi.batchApprove(ids),
    refresh: loadList,
    successText: (n) => `All ${n} bill${n === 1 ? "" : "s"} approved and posted.`,
    onRequestError: (err) =>
      toast.error(errorMessage(err, "Couldn't approve the selected bills. Try again.")),
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsBillsApi.list();
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your bills."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setOpenBill = (id: string | null) => {
    const updated = new URLSearchParams(params);
    if (id) updated.set("bill", id);
    else updated.delete("bill");
    setParams(updated, { replace: true });
  };

  if (openBill) {
    return (
      <BillReview
        billId={openBill}
        onBack={() => setOpenBill(null)}
        refreshList={loadList}
      />
    );
  }

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }

  if (!rows) {
    return <SkeletonList rows={4} data-testid="tps-bills-loading" />;
  }

  const selectableIds = rows
    .filter((bill) => bill.status === "TpsApprovalPending")
    .map((bill) => bill.id);
  const approveLabel = `Approve & post ${batch.selected.size} bill${
    batch.selected.size === 1 ? "" : "s"
  }`;

  return (
    <section aria-label="Bills" className="flex flex-col gap-space-4">
      <BatchResultBanner batch={batch} testIdPrefix="tps-bills-batch" />
      {rows.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No bills yet"
          description="Monthly statements Phoenix prepares on your behalf will appear here for your review and approval before the borrower sees them."
        />
      ) : (
        <>
          <BatchSelectionHeader
            batch={batch}
            selectableIds={selectableIds}
            selectAllLabel="Select all awaiting approval"
            actionLabel={approveLabel}
            testIdPrefix="tps-bills-batch"
          />
          <div className="flex flex-col gap-space-3">
            {rows.map((bill) => (
              <div key={bill.id} className="flex flex-col gap-space-1">
                <div className="flex items-start gap-space-2">
                  {bill.status === "TpsApprovalPending" && (
                    <BatchRowCheckbox
                      id={bill.id}
                      batch={batch}
                      label={`Select bill ${bill.loanNumber} — ${periodLabel(bill.periodEnd)}`}
                      testIdPrefix="tps-bills-batch"
                    />
                  )}
                  <ListCard
                    className="min-w-0 flex-1"
                    data-testid={`tps-bill-row-${bill.id}`}
                    title={`${bill.loanNumber} — ${periodLabel(bill.periodEnd)}`}
                    status={
                      <StatusBadge tone={STATUS_TONE[bill.status]}>
                        {STATUS_LABEL[bill.status]}
                      </StatusBadge>
                    }
                    meta={
                      <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
                        <span>{bill.borrowerName || "—"}</span>
                        <span aria-hidden>·</span>
                        <span>{formatCurrency(bill.amountDue)} due</span>
                        <span aria-hidden>·</span>
                        <span>
                          Prepared <Timestamp value={bill.preparedAt} />
                        </span>
                        {bill.payoutState ||
                        (bill.status as string) === "Paid" ||
                        bill.paymentStatus === "Cleared" ||
                        bill.paymentStatus === "Processing" ? (
                          <>
                            <span aria-hidden>·</span>
                            <BillPaymentIndicator bill={bill} />
                          </>
                        ) : null}
                      </span>
                    }
                    actions={
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setOpenBill(bill.id)}
                        data-testid={`tps-bill-open-${bill.id}`}
                      >
                        {bill.status === "TpsApprovalPending" ? "Review" : "View"}
                      </Button>
                    }
                  />
                </div>
                <BatchRowError
                  error={batch.rowErrors.get(bill.id)}
                  testId={`tps-bills-batch-error-${bill.id}`}
                />
              </div>
            ))}
          </div>
          <BatchConfirmDialog
            batch={batch}
            title="Approve & post bills"
            body={`Approve and post ${batch.selected.size} bill${
              batch.selected.size === 1 ? "" : "s"
            }? Each bill becomes visible to its borrower once posted.`}
            confirmLabel={approveLabel}
            testIdPrefix="tps-bills-batch"
          />
        </>
      )}
    </section>
  );
}

function BillReview({
  billId,
  onBack,
  refreshList,
}: {
  billId: string;
  onBack: () => void;
  refreshList: () => Promise<unknown>;
}) {
  const toast = useToast();
  const [bill, setBill] = useState<TpsBill | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [returning, setReturning] = useState(false);
  const [note, setNote] = useState("");
  // WS5 — the Phoenix↔servicer return conversation (chain-keyed, so every
  // correction round of this billing period reads as one thread). Non-fatal.
  const [comments, setComments] = useState<TpsBillComment[]>([]);

  const loadComments = useCallback(async () => {
    try {
      setComments(await tpsBillsApi.comments(billId));
    } catch {
      /* thread is non-fatal — the firm can still approve/return */
    }
  }, [billId]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await tpsBillsApi.get(billId);
        if (!cancelled) setBill(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load this bill."));
      }
    })();
    void loadComments();
    return () => {
      cancelled = true;
    };
  }, [billId, loadComments]);

  // Fetch the rendered PDF as an object URL for the iframe (revoked on unmount).
  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const blob = await tpsBillsApi.pdf(billId);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } catch {
        /* preview is non-fatal — the firm can still approve/return */
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [billId]);

  const approve = useSubmittingMutation(() => tpsBillsApi.approve(billId), {
    refresh: refreshList,
    onSuccess: (updated) => {
      setBill(updated);
      toast.success("Approved and posted to the borrower.");
    },
    onError: (err) => toast.error(errorMessage(err, "Couldn't approve. Try again.")),
  });

  const submitReturn = useSubmittingMutation(() => tpsBillsApi.return(billId, note), {
    refresh: refreshList,
    onSuccess: (updated) => {
      setBill(updated);
      setReturning(false);
      setNote("");
      toast.success("Returned to Phoenix for correction.");
      void loadComments(); // the note lands on the conversation thread
    },
    onError: (err) => toast.error(errorMessage(err, "Couldn't return the bill. Try again.")),
  });

  const backButton = (
    <Button type="button" variant="text" size="sm" onClick={onBack} data-testid="tps-bill-back">
      <ArrowLeft size={16} aria-hidden />
      Back to bills
    </Button>
  );

  if (error) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <p className="text-small text-status-error" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="flex flex-col gap-space-3">
        {backButton}
        <DetailSection>
          <SkeletonText lines={6} />
        </DetailSection>
      </div>
    );
  }

  const pending = approve.pending || submitReturn.pending;
  const actionable = bill.status === "TpsApprovalPending";

  return (
    <section
      aria-label={`Bill for ${bill.loanNumber}, ${periodLabel(bill.periodEnd)}`}
      className="flex flex-col gap-space-4"
    >
      <div className="flex items-center justify-between gap-space-3">
        {backButton}
        <StatusBadge tone={STATUS_TONE[bill.status]}>{STATUS_LABEL[bill.status]}</StatusBadge>
      </div>

      <div>
        <h2 className="m-0 font-display text-h3 font-bold text-onyx">
          {bill.loanNumber} — {periodLabel(bill.periodEnd)}
        </h2>
        <p className="mt-space-1 text-small text-gray-mid">
          {bill.borrowerName || "—"} · Prepared <Timestamp value={bill.preparedAt} />
          {bill.postedAt && (
            <>
              {" · Posted "}
              <Timestamp value={bill.postedAt} />
            </>
          )}
        </p>
      </div>

      {bill.status === "Returned" && bill.returnedReason && (
        <div
          role="note"
          className="rounded-md border border-status-error bg-status-error/10 p-space-3 text-small text-onyx"
        >
          <span className="font-semibold">Returned for correction:</span> {bill.returnedReason}
        </div>
      )}

      <DetailSection title="Amount due">
        <dl className="grid grid-cols-2 gap-x-space-4 gap-y-space-2 text-small">
          <Line label="Principal" value={formatCurrency(bill.principal)} />
          <Line label="Interest" value={formatCurrency(bill.interest)} />
          <Line label="Servicing fee" value={formatCurrency(bill.servicingFee)} />
          {/* BLA Core §2.4 — shown only when charged, so a loan without the
              fee renders exactly the three lines it always did. */}
          {bill.paymentAdministrationFee > 0 && (
            <Line
              label="Payment administration fee"
              value={formatCurrency(bill.paymentAdministrationFee)}
            />
          )}
          <Line label="Total amount due" value={formatCurrency(bill.amountDue)} strong />
        </dl>
      </DetailSection>

      <DetailSection title="Statement">
        {pdfUrl ? (
          <iframe
            title="Statement PDF"
            src={pdfUrl}
            data-testid="tps-bill-pdf"
            className="h-[640px] w-full rounded-md border border-border-light bg-white-pure"
          />
        ) : (
          <SkeletonText lines={4} />
        )}
      </DetailSection>

      {comments.length > 0 && (
        <DetailSection title="Conversation with Phoenix">
          <ul
            className="m-0 flex list-none flex-col gap-space-3 p-0"
            data-testid="tps-bill-thread"
          >
            {comments.map((c) => (
              <li
                key={c.id}
                className="rounded-md border border-border-light bg-white-warm p-space-3"
                data-testid={`tps-bill-comment-${c.id}`}
              >
                <p className="m-0 text-caption uppercase tracking-label text-gray-mid">
                  {c.authorName}
                  {" · "}
                  <Timestamp value={c.createdAt} />
                </p>
                <p className="m-0 mt-space-1 text-small text-onyx whitespace-pre-wrap">
                  {c.body}
                </p>
              </li>
            ))}
          </ul>
          <p className="mt-space-2 text-caption text-gray-mid">
            Returning this bill adds your note to the conversation; Phoenix
            replies here when they correct and resubmit.
          </p>
        </DetailSection>
      )}

      {actionable && (
        <DetailSection title="Your decision">
          {returning ? (
            <div className="flex flex-col gap-space-3">
              <label className="flex flex-col gap-space-1">
                <span className="text-caption text-gray-mid uppercase tracking-label">
                  Reason for returning
                </span>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={4}
                  disabled={pending}
                  invalid={note.trim().length === 0}
                  aria-label="Reason for returning"
                  data-testid="tps-bill-return-note"
                />
              </label>
              <div className="flex flex-wrap items-center gap-space-2">
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => void submitReturn.submit()}
                  disabled={pending || note.trim().length === 0}
                  loading={submitReturn.pending}
                  data-testid="tps-bill-return-submit"
                >
                  Return to Phoenix
                </Button>
                <Button
                  type="button"
                  variant="text"
                  size="sm"
                  onClick={() => {
                    setReturning(false);
                    setNote("");
                  }}
                  disabled={pending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-space-2">
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => void approve.submit()}
                disabled={pending}
                loading={approve.pending}
                data-testid="tps-bill-approve"
              >
                Approve &amp; post
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setReturning(true)}
                disabled={pending}
                data-testid="tps-bill-return"
              >
                Return for correction
              </Button>
            </div>
          )}
        </DetailSection>
      )}
    </section>
  );
}

function Line({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <>
      <dt className={strong ? "font-semibold text-onyx" : "text-gray-mid"}>{label}</dt>
      <dd className={`m-0 text-right ${strong ? "font-semibold text-onyx" : "text-onyx"}`}>
        {value}
      </dd>
    </>
  );
}
